import React, { useState, useEffect, useRef } from 'react'
import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage'

import { SendMessage as BLSendMessage } from './../../../utilities/BL/Messages'


// ==============================================================================
// CONFIGURACIÓN
// ==============================================================================
const COOLDOWN_STORAGE_KEY = "fanmessages_next_allowed_ts";
const DEFAULT_COOLDOWN_MS = 2 * 60 * 1000; // 2 minutos

// Utilidad: Formatea ms → "m:ss"
function msToMinSec(ms) {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const Messages = ({ cooldownMs = DEFAULT_COOLDOWN_MS }) => {
  const { showMessage } = useFloatMessage();

  // --- VISTAS ---
  const [view, setView] = useState("intro"); // 'intro' | 'form' | 'sent'

  // --- ESTADOS ---
  const [formData, setFormData] = useState({ message: '', sender: '', phone: '' });
  const [isSending, setIsSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // --- COOLDOWN ---
  const [nextAllowedTs, setNextAllowedTs] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);

  // Cargar cooldown al montar
  useEffect(() => {
    const saved = localStorage.getItem(COOLDOWN_STORAGE_KEY);
    if (saved) {
      const ts = parseInt(saved, 10);
      if (!Number.isNaN(ts)) setNextAllowedTs(ts);
    }
  }, []);

  // Timer del cooldown
  useEffect(() => {
    const tick = () => {
      if (!nextAllowedTs) {
        setRemainingMs(0);
        return;
      }
      const diff = nextAllowedTs - Date.now();
      setRemainingMs(Math.max(0, diff));
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [nextAllowedTs]);

  const canStartNew = !nextAllowedTs || Date.now() >= nextAllowedTs;

  // --- HANDLERS ---

  const handleStart = () => {
    if (!canStartNew) {
        showMessage({ type: MESSAGE_TYPE.INFO, text: `Espera ${msToMinSec(remainingMs)} para enviar otra dedicatoria.` });
        return;
    }
    setView("form");
  };

  const handleChange = (e) => {
    const { name, value } = e.target
    
    // Validación: Mensaje (Máx 100)
    if (name === 'message' && value.length > 100) return
    
    // Validación: Remitente (Máx 20)
    if (name === 'sender' && value.length > 20) return

    // Validación: Teléfono (Solo números y Máx 14)
    if (name === 'phone') {
        // Si el usuario intenta escribir algo que no es número, lo ignoramos
        if (!/^\d*$/.test(value)) return
        if (value.length > 14) return
    }
    
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validación visual rápida
    if (!formData.message.trim()) {
        setErrorMsg("Escribe tu mensaje")
        return
    }

    // Validación visual de teléfono (si escribió algo, que sean al menos 10)
    if (formData.phone && formData.phone.length < 10) {
        setErrorMsg("El teléfono debe tener al menos 10 dígitos")
        return
    }

    setIsSending(true)
    setErrorMsg(null)

    try {
        const response = await BLSendMessage(formData)

        if (response.success) {
            // Activar Cooldown
            const ts = Date.now() + cooldownMs
            setNextAllowedTs(ts)
            localStorage.setItem(COOLDOWN_STORAGE_KEY, String(ts))
            
            // Guardar flag temporal para mostrar mensaje condicional en vista 'sent'
            // (Usamos una variable local o el estado actual antes de limpiarlo)
            const phoneWasSent = formData.phone.length > 0
            
            // Limpieza
            setFormData({ message: '', sender: '', phone: '' })
            
            // Cambiar vista (Pasamos el flag phoneWasSent vía estado de navegación si fuera router, 
            // pero aquí es SPA simple, así que podemos usar un estado temporal o derivarlo en el render si guardamos el último envío.
            // Para simplificar, usaremos un estado simple 'hasSentPhone' solo para la UI de éxito).
            setHasSentPhone(phoneWasSent) 
            setView("sent")
        } else {
            setErrorMsg(response.message)
            showMessage({ type: MESSAGE_TYPE.BAD, text: response.message })
        }

    } catch (err) {
        console.error(err)
        setErrorMsg("Error inesperado. Intenta de nuevo.")
        showMessage({ type: MESSAGE_TYPE.BAD, text: "Ocurrió un error inesperado." })
    } finally {
        setIsSending(false)
    }
  }

  const [hasSentPhone, setHasSentPhone] = useState(false)


  return (
    <div className="container SelfieFan Messages__Fan">
      
      {/* VISTA 1: INTRODUCCIÓN */}
      {view === "intro" && (
        <div className="SelfieFan__Intro container pt-4">
            <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />
            <p className="SelfieFan__Fan__Title">¡Bienvenid@ Aficionad@!</p>
            <p className="SelfieFan__Fan__Text--Center">¡Apoyemos a nuestro equipo!</p>

            <ol className="SelfieFan__List">
              <li>Escribe el mensaje</li>
              <li>Escribe tu nombre (opcional)</li>
              <li>Da clic en “Enviar mi mensaje</li>
            </ol>

            <p className='Messages__Fan__Text--Center'>Tu mensaje podría aparecer en pantalla mostrando tu apoyo al equipo</p>

            <div className="SelfieFan__Intro__Actions">
              <button className="Btn__Adsl--Red" onClick={handleStart} disabled={!canStartNew} title={!canStartNew ? `Espera ${msToMinSec(remainingMs)}` : ""}>
                {!canStartNew ? `Puedes enviar otra en ${msToMinSec(remainingMs)}` : <><i className='bi bi-chat'></i> Comenzar</>}
              </button>
            </div>
        </div>
      )}

      {/* VISTA 2: FORMULARIO DE CAPTURA */}
      {view === "form" && (
        <div className="Messages__Fan__FormContainer pt-4">
          <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />
          
          <p className="Messages__Fan__Text--Title Messages__Fan__Text--Center Messages__Fan__Element--SpaceUp">Envía tu mensaje de apoyo</p>

          <form className='Messages__Fan__FormContainer__Form' onSubmit={handleSubmit}>
            
            {/* Campo Mensaje */}
            <label className='Messages__Fan__FormContainer__Form--SpaceUp'>Escribe tu mensaje</label>
            <textarea className="form-control" rows="3" name="message" value={formData.message} onChange={handleChange} placeholder="Escribe tu mensaje aquí" maxLength={100} disabled={isSending}></textarea>
            <p className="text-end small">{formData.message.length}/100</p>
            
            {/* Campo Remitente */}
            <label className='Messages__Fan__FormContainer__Form--SpaceUp'>¿Quién envía el mensaje? (opcional)</label>
            <input type="text" className="form-control" name="sender" value={formData.sender} onChange={handleChange} placeholder="Escribe tu nombre" maxLength={20} disabled={isSending}/>
            <div className="text-end small">{formData.sender.length}/20</div>

            {/* Teléfono */}
            {/* <label className='Messages__Fan__FormContainer__Form--SpaceUp'>¿Te gustaría que te ayudemos con tu propuesta o a declarártele a esa persona especial? (opcional)</label>
            <p className='small text-muted mb-1'>Escribe tu WhatsApp o teléfono. Podríamos ayudarte.</p>
            <input type="tel" className="form-control" name="phone" value={formData.phone} onChange={handleChange} placeholder="Ej. 4441234567" maxLength={14} disabled={isSending}/>
            <div className="text-end small">{formData.phone.length}/14</div> */}
            
            {/* Botones de Acción */}
            <button type="submit" className="Btn__Adsl--Red Messages__Fan__FormContainer__Form--SpaceUp" disabled={isSending || !formData.message.trim()}>
              {isSending ? "Enviando..." : <><i className='bi bi-chat'></i> Enviar mi mensaje</>}
            </button>
            <button type="button" className="Btn__Adsl--RedOutline Messages__Fan__FormContainer__Form--SpaceUp" onClick={() => setView("intro")} disabled={isSending}>Salir</button>
            
            {/* Mensajes de Error */}
            {errorMsg && (<p className="Messages__Fan__Text--Center text-danger mt-3 fw-bold">{errorMsg}</p>)}
          </form>
        </div>
      )}

      {/* VISTA 3: CONFIRMACIÓN Y COOLDOWN */}
      {view === "sent" && (
        <div className="SelfieFan__Sent container">
          <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />
          <p className="fs-1 mb-0 text-success"><i className="bi bi-check-circle-fill"></i></p>
          <p className="SelfieFan__Sent__Title">¡Mensaje enviado!</p>
          <p className='Messages__Fan__Text--Center'>Tu mensaje ha sido enviado correctamente</p>

          {/* Mensaje Condicional: Si envió teléfono */}
          {/* {hasSentPhone && (
            <p className='Messages__Fan__Text--Center'>A la brevedad posible nos comunicaremos contigo por teléfono o WhatsApp <i className="bi bi-emoji-wink"></i></p>
          )} */}

          <div className="SelfieFan__Sent__Actions justify-content-center">
            <button className="Btn__Adsl--RedOutline" onClick={() => canStartNew && setView("intro")} disabled={!canStartNew}>
              {canStartNew ? <><i className='bi bi-chat'></i> Enviar otro mensaje</> : `Puedes enviar otro en ${msToMinSec(remainingMs)}`}
            </button>
          </div>

          <hr />
          
          <a href='https://interaktia.com/?utm_source=messages_web&utm_medium=banner&utm_campaign=fdm_santoscb' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
          </a>
          <a href='https://interaktia.com/?utm_source=messages_web&utm_medium=banner&utm_campaign=fdm_santoscb' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

          <hr />

          <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
          <p className='text-center mb-0'>Five Digital Minds está listo para ti</p>
          <a href='https://interaktia.com/?utm_source=messages_web&utm_medium=banner_text&utm_campaign=fdm_santoscb' target='_blank' className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
        </div>
      )}

      <footer className="text-center mt-3 FooterCredits">
        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=messages_web&utm_medium=footer&utm_campaign=fdm_santoscb' target='_blank' className='text-dark'>Five Digital Minds</a> | 2026
      </footer>
    </div>
  );
};

export { Messages };









// PLANTILLA DE: Día del Amor y la Amistad - Día de San Valentín 2026
// return (
//   <div className="container SelfieFan Messages__Fan">
    
//     {/* VISTA 1: INTRODUCCIÓN */}
//     {view === "intro" && (
//       <div className="SelfieFan__Intro container pt-4">
//           <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />
//           <p className="SelfieFan__Fan__Title">¡Bienvenid@ Aficionad@!</p>
//           <p className="SelfieFan__Fan__Text--Center">Envía tu dedicatoria a la pantalla para tu amig@ o a esa persona especial</p>

//           <ol className="SelfieFan__List">
//             <li>Escribe el mensaje</li>
//             <li>Escribe tu nombre (opcional)</li>
//             <li>Da clic en “Enviar dedicatoria”</li>
//           </ol>

//           <p className='Messages__Fan__Text--Center'>Tu mensaje podría aparecer en pantalla mostrando tu dedicatoria</p>

//           <div className="SelfieFan__Intro__Actions">
//             <button className="Btn__Adsl--Red" onClick={handleStart} disabled={!canStartNew} title={!canStartNew ? `Espera ${msToMinSec(remainingMs)}` : ""}>
//               {!canStartNew ? `Puedes enviar otra en ${msToMinSec(remainingMs)}` : <><i className='bi bi-chat-heart'></i> Comenzar</>}
//             </button>
//           </div>
//       </div>
//     )}

//     {/* VISTA 2: FORMULARIO DE CAPTURA */}
//     {view === "form" && (
//       <div className="Messages__Fan__FormContainer pt-4">
//         <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />
        
//         <p className="Messages__Fan__Text--Title Messages__Fan__Text--Center Messages__Fan__Element--SpaceUp">Escribe tu dedicatoria</p>

//         <form className='Messages__Fan__FormContainer__Form' onSubmit={handleSubmit}>
          
//           {/* Campo Mensaje */}
//           <label className='Messages__Fan__FormContainer__Form--SpaceUp'>¿Qué le quieres decir a tu amig@ o a esa persona especial?</label>
//           <textarea className="form-control" rows="3" name="message" value={formData.message} onChange={handleChange} placeholder="Escribe tu mensaje aquí" maxLength={100} disabled={isSending}></textarea>
//           <p className="text-end small">{formData.message.length}/100</p>
          
//           {/* Campo Remitente */}
//           <label className='Messages__Fan__FormContainer__Form--SpaceUp'>¿Quién envía la dedicatoria? (opcional)</label>
//           <input type="text" className="form-control" name="sender" value={formData.sender} onChange={handleChange} placeholder="Escribe tu nombre" maxLength={20} disabled={isSending}/>
//           <div className="text-end small">{formData.sender.length}/20</div>

//           {/* Teléfono */}
//           <label className='Messages__Fan__FormContainer__Form--SpaceUp'>¿Te gustaría que te ayudemos con tu propuesta o a declarártele a esa persona especial? (opcional)</label>
//           <p className='small text-muted mb-1'>Escribe tu WhatsApp o teléfono. Podríamos ayudarte.</p>
//           <input type="tel" className="form-control" name="phone" value={formData.phone} onChange={handleChange} placeholder="Ej. 4441234567" maxLength={14} disabled={isSending}/>
//           <div className="text-end small">{formData.phone.length}/14</div>
          
//           {/* Botones de Acción */}
//           <button type="submit" className="Btn__Adsl--Red Messages__Fan__FormContainer__Form--SpaceUp" disabled={isSending || !formData.message.trim()}>
//             {isSending ? "Enviando..." : <><i className='bi bi-chat-heart'></i> Enviar mi dedicatoria</>}
//           </button>
//           <button type="button" className="Btn__Adsl--RedOutline Messages__Fan__FormContainer__Form--SpaceUp" onClick={() => setView("intro")} disabled={isSending}>Cancelar</button>
          
//           {/* Mensajes de Error */}
//           {errorMsg && (<p className="Messages__Fan__Text--Center text-danger mt-3 fw-bold">{errorMsg}</p>)}
//         </form>
//       </div>
//     )}

//     {/* VISTA 3: CONFIRMACIÓN Y COOLDOWN */}
//     {view === "sent" && (
//       <div className="SelfieFan__Sent container">
//         <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />
//         <p className="fs-1 mb-0 text-success"><i className="bi bi-check-circle-fill"></i></p>
//         <p className="SelfieFan__Sent__Title">¡Dedicatoria enviada!</p>
//         <p className='Messages__Fan__Text--Center'>Tu dedicatoria ha sido enviada correctamente</p>

//         {/* Mensaje Condicional: Si envió teléfono */}
//         {hasSentPhone && (
//           <p className='Messages__Fan__Text--Center'>A la brevedad posible nos comunicaremos contigo por teléfono o WhatsApp <i className="bi bi-emoji-wink"></i></p>
//         )}

//         <div className="SelfieFan__Sent__Actions justify-content-center">
//           <button className="Btn__Adsl--RedOutline" onClick={() => canStartNew && setView("intro")} disabled={!canStartNew}>
//             {canStartNew ? <><i className='bi bi-chat-heart'></i> Enviar otra dedicatoria</> : `Puedes enviar otra en ${msToMinSec(remainingMs)}`}
//           </button>
//         </div>

//         <hr />
        
//         <a href='https://interaktia.com/?utm_source=messages_web&utm_medium=banner&utm_campaign=fdm_santoscb' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
//           <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
//         </a>
//         <a href='https://interaktia.com/?utm_source=messages_web&utm_medium=banner&utm_campaign=fdm_santoscb' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

//         <hr />

//         <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
//         <p className='text-center mb-0'>Five Digital Minds está listo para ti</p>
//         <a href='https://interaktia.com/?utm_source=messages_web&utm_medium=banner_text&utm_campaign=fdm_santoscb' target='_blank' className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
//       </div>
//     )}

//     <footer className="text-center mt-3 FooterCredits">
//       Hecho con ♥ por <a href='https://interaktia.com/?utm_source=messages_web&utm_medium=footer&utm_campaign=fdm_santoscb' target='_blank' className='text-dark'>Five Digital Minds</a> | 2026
//     </footer>
//   </div>
// );